import { AnyAction } from 'redux';

import { HomographyActions, HomographyCamera } from '../../actions/homographyActions';
import { CalibrationType } from '../../interfaces/homography';
import { deepCopy } from '../../utils';
import { HomographyState, initialHomographyState } from './homographyState';

const updatePoints = (
  state: HomographyState,
  points: any[],
  calibrationType: CalibrationType,
  cameraType: HomographyCamera,
): HomographyState => {
  const calibrationMap = new Map<string, (x: any) => HomographyState>([
    [
      CalibrationType.LPR,
      (state) => {
        switch (cameraType) {
          case HomographyCamera.CONTEXT:
            return {
              ...state,
              lPRPairs: {
                ...state.lPRPairs,
                imagePairs: {
                  ...state.lPRPairs.imagePairs,
                  imageOne: {
                    name: 'context',
                    points: deepCopy(points),
                  },
                },
              },
            } as HomographyState;
          case HomographyCamera.LPR:
            return {
              ...state,
              lPRPairs: {
                ...state.lPRPairs,
                imagePairs: {
                  ...state.lPRPairs.imagePairs,
                  imageTwo: {
                    name: 'lpr',
                    points: deepCopy(points),
                  },
                },
              },
            } as HomographyState;
        }
      },
    ],
    [
      CalibrationType.NIGHTLPR,
      () => {
        switch (cameraType) {
          case HomographyCamera.CONTEXT:
            return {
              ...state,
              nightLPRPairs: {
                ...state.nightLPRPairs,
                imagePairs: {
                  ...state.nightLPRPairs.imagePairs,
                  imageOne: {
                    name: 'context',
                    points: deepCopy(points),
                  },
                },
              },
            } as HomographyState;
          case HomographyCamera.LPR:
            return {
              ...state,
              nightLPRPairs: {
                ...state.nightLPRPairs,
                imagePairs: {
                  ...state.nightLPRPairs.imagePairs,
                  imageTwo: {
                    name: 'lpr',
                    points: deepCopy(points),
                  },
                },
              },
            } as HomographyState;
        }
      },
    ],
    [
      CalibrationType.MAP,
      () => {
        switch (cameraType) {
          case HomographyCamera.CONTEXT:
            return {
              ...state,
              mapPairs: {
                ...state.mapPairs,
                imagePairs: {
                  ...state.mapPairs.imagePairs,
                  imageOne: {
                    ...state.mapPairs.imagePairs.imageOne,
                    points: deepCopy(points),
                  },
                },
              },
            } as HomographyState;
          case HomographyCamera.MAP:
            return {
              ...state,
              mapPairs: {
                ...state.mapPairs,
                imagePairs: {
                  ...state.mapPairs.imagePairs,
                  imageTwo: {
                    ...state.mapPairs.imagePairs.imageTwo,
                    points: deepCopy(points),
                  },
                },
              },
            } as HomographyState;
        }
      },
    ],
  ]);

  return calibrationMap.get(calibrationType)(state);
};

const updateHomographyMatrix = (state: HomographyState, calibrationType: CalibrationType, matrix: Float32Array) => {
  switch (calibrationType) {
    case CalibrationType.LPR:
      return {
        ...state,
        lPRPairs: {
          ...state.lPRPairs,
          calculatedHomography: matrix,
        },
      } as HomographyState;
    case CalibrationType.NIGHTLPR:
      return {
        ...state,
        nightLPRPairs: {
          ...state.nightLPRPairs,
          calculatedHomography: matrix,
        },
      } as HomographyState;
    case CalibrationType.MAP:
      return {
        ...state,
        mapPairs: {
          ...state.mapPairs,
          calculatedHomography: matrix,
        },
      } as HomographyState;
    default:
      return state;
  }
};

const deleteCalculatedHomographyMatrix = (state: HomographyState, calibrationType: CalibrationType) => {
  switch (calibrationType) {
    case CalibrationType.LPR:
      state.lPRPairs.calculatedHomography = null;
      return state;
    case CalibrationType.NIGHTLPR:
      state.nightLPRPairs.calculatedHomography = null;
      return state;
    case CalibrationType.MAP:
      state.mapPairs.calculatedHomography = null;
      return state;
    default:
      return state;
  }
};

export const homographyReducer = (state: HomographyState = initialHomographyState, action: AnyAction) => {
  switch (action.type) {
    case HomographyActions.UPDATE_LPR_CONTEXT_POINTS:
      return updatePoints(state, action.payload, CalibrationType.LPR, HomographyCamera.CONTEXT);
    case HomographyActions.UPDATE_LPR_POINTS:
      return updatePoints(state, action.payload, CalibrationType.LPR, HomographyCamera.LPR);
    case HomographyActions.UPDATE_NIGHT_LPR_CONTEXT_POINTS:
      return updatePoints(state, action.payload, CalibrationType.NIGHTLPR, HomographyCamera.CONTEXT);
    case HomographyActions.UPDATE_NIGHT_LPR_POINTS:
      return updatePoints(state, action.payload, CalibrationType.NIGHTLPR, HomographyCamera.LPR);
    case HomographyActions.UPDATE_MAP_POINTS:
      return updatePoints(state, action.payload, CalibrationType.MAP, HomographyCamera.MAP);
    case HomographyActions.UPDATE_MAP_CONTEXT_POINTS:
      return updatePoints(state, action.payload, CalibrationType.MAP, HomographyCamera.CONTEXT);
    case HomographyActions.LPR_MODIFIED:
      return {
        ...state,
        lPRPairs: {
          ...state.lPRPairs,
          modified: action.payload,
        },
      } as HomographyState;
    case HomographyActions.NIGHT_LPR_MODIFIED:
      return {
        ...state,
        nightLPRPairs: {
          ...state.nightLPRPairs,
          modified: action.payload,
        },
      } as HomographyState;
    case HomographyActions.MAP_MODIFIED:
      return {
        ...state,
        mapPairs: {
          ...state.mapPairs,
          modified: action.payload,
        },
      } as HomographyState;
    case HomographyActions.CALCULATING_HOMOGRAPHY:
      return {
        ...state,
        isSubmitting: action.payload,
      } as HomographyState;
    case HomographyActions.UPDATE_CALCULATED_HOMOGRAPHY:
      const { payload } = action;
      return updateHomographyMatrix(state, payload.calibrationType, payload.transformMatrix);
    case HomographyActions.DELETE_CALCULATED_HOMOGRAPHY:
      return deleteCalculatedHomographyMatrix(state, action.payload);
    case HomographyActions.UPDATE_MAP_MARKERS:
      return {
        ...state,
        mapPairs: {
          ...state.mapPairs,
          activeMarkers: action.payload,
        },
      } as HomographyState;
    case HomographyActions.DELETE_MAP_MARKERS:
      return {
        ...state,
        mapPairs: {
          ...state?.mapPairs,
          activeMarkers: initialHomographyState.mapPairs.activeMarkers,
        },
      } as HomographyState;
    default:
      return {
        ...state,
      } as HomographyState;
  }
};
